import React, { useMemo } from 'react';
import styled from '@emotion/styled';
import { Button, Tag, Typography, theme } from '@robinpowered/ui-kit';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faChairOffice,
  faEdit,
  faTrash,
} from '@awesome.me/kit-cf858715fd/icons/classic/solid';
import { useTranslation } from 'react-i18next';
import moment from 'moment-timezone';
import { ReservationActionsMenu } from './ReservationActionsMenu';
import { ConfirmedCheck } from './ConfirmedCheck';
import { CheckInStatusTag } from './CheckInStatusTag';
import {
  ProfileReservation,
  ReservationMenuAction,
  getReservationActions,
} from '../../utils/getReservationActions';
import {
  formatReservationTimeRange,
  getScheduledDaysFromRecurrence,
} from '../../utils/formatBookings';
import { useDeskAllowsIssueReports } from '../../hooks/useDeskAllowsIssueReports';

type ReservationCardProps = {
  reservation: ProfileReservation;
  recurrence: string | null;
  isCurrentPerson: boolean;
  onCardClick: (reservation: ProfileReservation) => void;
  onCheckIn: (reservation: ProfileReservation) => void;
  onMenuAction: (
    action: ReservationMenuAction,
    reservation: ProfileReservation
  ) => void;
  /**
   * In the fullscreen Desks panel the card is wide, so actions lay out in a
   * single left-aligned row of auto-width buttons. In the narrow sider they
   * stack full-width.
   */
  wideLayout?: boolean;
  /**
   * Overrides the default meta line. The fullscreen Desks panel labels each
   * flex reservation with its date (e.g. "Tue, Apr 7, 9:00 am - 5:30 pm")
   * rather than the sider's "Today" range.
   */
  metaLabel?: string;
};

export const ReservationCard = ({
  reservation,
  recurrence,
  isCurrentPerson,
  onCardClick,
  onCheckIn,
  onMenuAction,
  wideLayout = false,
  metaLabel,
}: ReservationCardProps): JSX.Element => {
  const { t } = useTranslation('peopleProfileSidebar');
  const { token } = theme.useToken();

  const { seat } = reservation;
  const { allowed: canReportIssue } = useDeskAllowsIssueReports(seat.id);

  const actions = useMemo(
    () => getReservationActions(reservation, isCurrentPerson, canReportIssue),
    [reservation, isCurrentPerson, canReportIssue]
  );

  const scheduleDays = useMemo(() => {
    const days = getScheduledDaysFromRecurrence(recurrence);
    if (days.length === 7) {
      return t('bookings.everyDay');
    }
    return days.map((day) => t(`bookings.daysShort.${day}`)).join(', ');
  }, [recurrence, t]);

  const timeRange = useMemo(
    () =>
      formatReservationTimeRange(
        reservation.startTime,
        reservation.endTime,
        reservation.timeZone
      ),
    [reservation.startTime, reservation.endTime, reservation.timeZone]
  );

  const deskName = seat.zone?.name
    ? t('bookings.deskWithZone', { desk: seat.name, zone: seat.zone.name })
    : seat.name;

  const floorLocation = seat.level?.name
    ? t('bookings.floorLocation', {
        floor: seat.level.name,
        location: seat.location.name,
      })
    : seat.location.name;

  const firstName = reservation.reservee?.user?.name?.split(' ')[0] ?? '';

  const checkInLabel =
    isCurrentPerson || !firstName
      ? t('bookings.checkIn')
      : t('bookings.checkInForUser', { name: firstName });

  const bottomActions = actions.menuActions.filter(isBottomAction);
  const overflowActions = actions.menuActions.filter(
    (action) => !isBottomAction(action)
  );
  const stackedActions = bottomActions.filter((action) => action === 'release');
  const pairedActions = bottomActions.filter((action) => action !== 'release');
  const cancelButtonLabel = isReservationInProgress(reservation)
    ? t('bookings.cancelReservationModal.end_booking')
    : t('bookings.menu.cancel');

  return (
    <Card $borderColor={token.colorBorderSecondary}>
      {overflowActions.length > 0 && (
        <MenuSlot>
          <ReservationActionsMenu
            actions={overflowActions}
            onAction={(action) => onMenuAction(action, reservation)}
          />
        </MenuSlot>
      )}

      <Meta>
        {actions.kind === 'assignment' ? (
          <>
            <AssignedTag>{t('bookings.assigned')}</AssignedTag>
            {scheduleDays && (
              <Typography.Text type="secondary">{scheduleDays}</Typography.Text>
            )}
          </>
        ) : (
          <Typography.Text type="secondary">
            {metaLabel ?? t('bookings.todayRange', { range: timeRange })}
          </Typography.Text>
        )}
      </Meta>

      <Body>
        <DeskIconWrapper
          $background={token.colorFillSecondary}
          $color={token.colorIcon}
        >
          <FontAwesomeIcon icon={faChairOffice} />
          {actions.isCheckedIn && (
            <CheckOverlay>
              <ConfirmedCheck size={14} />
            </CheckOverlay>
          )}
        </DeskIconWrapper>
        <DeskText>
          <DeskNameButton
            type="button"
            onClick={() => onCardClick(reservation)}
            aria-label={t('bookings.viewDeskLabel', { desk: deskName })}
          >
            {deskName}
          </DeskNameButton>
          <Typography.Text type="secondary">{floorLocation}</Typography.Text>
        </DeskText>
      </Body>

      <CheckInStatusTag status={actions.checkInStatus} />

      {(actions.showCheckIn || bottomActions.length > 0) &&
        (wideLayout ? (
          // Fullscreen Desks panel: one left-aligned row of auto-width buttons.
          <ActionRow>
            {actions.showCheckIn && (
              <ActionButton
                type="primary"
                onClick={(event) => {
                  event.stopPropagation();
                  onCheckIn(reservation);
                }}
              >
                {checkInLabel}
              </ActionButton>
            )}
            {bottomActions.map((action) => (
              <ActionButton
                key={action}
                type="default"
                danger={action === 'cancel'}
                icon={getActionIcon(action)}
                onClick={(event) => {
                  event.stopPropagation();
                  onMenuAction(action, reservation);
                }}
              >
                {action === 'cancel'
                  ? cancelButtonLabel
                  : t(`bookings.menu.${action}`)}
              </ActionButton>
            ))}
          </ActionRow>
        ) : (
          <ActionButtonsContainer>
            {actions.showCheckIn && (
              <ActionButton
                block
                type="primary"
                onClick={(event) => {
                  event.stopPropagation();
                  onCheckIn(reservation);
                }}
              >
                {checkInLabel}
              </ActionButton>
            )}
            {stackedActions.map((action) => (
              <ActionButton
                key={action}
                block
                type="default"
                icon={getActionIcon(action)}
                onClick={(event) => {
                  event.stopPropagation();
                  onMenuAction(action, reservation);
                }}
              >
                {t(`bookings.menu.${action}`)}
              </ActionButton>
            ))}
            {pairedActions.length > 0 && (
              <ActionRow>
                {pairedActions.map((action) => (
                  <ActionButton
                    key={action}
                    $fill
                    type="default"
                    danger={action === 'cancel'}
                    icon={getActionIcon(action)}
                    onClick={(event) => {
                      event.stopPropagation();
                      onMenuAction(action, reservation);
                    }}
                  >
                    {action === 'cancel'
                      ? cancelButtonLabel
                      : t(`bookings.menu.${action}`)}
                  </ActionButton>
                ))}
              </ActionRow>
            )}
          </ActionButtonsContainer>
        ))}
    </Card>
  );
};

type BottomAction = Extract<
  ReservationMenuAction,
  'edit' | 'release' | 'cancel'
>;

const isBottomAction = (
  action: ReservationMenuAction
): action is BottomAction =>
  action === 'edit' || action === 'release' || action === 'cancel';

const getActionIcon = (action: BottomAction): JSX.Element | undefined => {
  if (action === 'edit') {
    return <FontAwesomeIcon icon={faEdit} />;
  }
  return action === 'cancel' ? <FontAwesomeIcon icon={faTrash} /> : undefined;
};

const isReservationInProgress = (reservation: ProfileReservation): boolean => {
  const zone =
    reservation.timeZone ?? reservation.seat.location.timezone ?? 'UTC';
  const now = moment.tz(zone);
  return (
    moment.tz(reservation.startTime, zone).isSameOrBefore(now) &&
    (!reservation.endTime || moment.tz(reservation.endTime, zone).isAfter(now))
  );
};

const Card = styled.div<{ $borderColor: string }>`
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 16px;
  border: 1px solid ${({ $borderColor }) => $borderColor};
  border-radius: 8px;
`;

const MenuSlot = styled.div`
  position: absolute;
  top: 8px;
  right: 8px;
`;

const Meta = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  min-height: 24px;
  padding-right: 28px;
`;

const AssignedTag = styled(Tag)`
  margin: 0;
`;

const Body = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 12px;
`;

const DeskIconWrapper = styled.span<{ $background: string; $color: string }>`
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 4px;
  flex-shrink: 0;
  font-size: 18px;
  background: ${({ $background }) => $background};
  color: ${({ $color }) => $color};
`;

const CheckOverlay = styled.span`
  position: absolute;
  bottom: -4px;
  right: -4px;
  display: inline-flex;
`;

const DeskText = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  flex: 1;
  min-width: 0;
  word-break: break-word;
`;

// Only the desk name navigates to the desk on the map; the card itself is not
// interactive. Rendered as a button that reads as plain text until hovered.
const DeskNameButton = styled.button`
  align-self: flex-start;
  max-width: 100%;
  margin: 0;
  padding: 0;
  border: none;
  background: none;
  font: inherit;
  font-size: 16px;
  color: inherit;
  text-align: left;
  word-break: break-word;
  cursor: pointer;

  &:hover,
  &:focus-visible {
    color: var(--ant-color-link, var(--ant-color-primary, #be1f77));
    text-decoration: underline;
  }

  &:focus-visible {
    outline: 2px solid var(--ant-color-primary, #eb2f96);
    outline-offset: 1px;
    border-radius: 2px;
  }
`;

const ActionButtonsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const ActionButton = styled(Button, {
  shouldForwardProp: (prop) => prop !== '$fill',
})<{ $fill?: boolean }>`
  ${({ $fill }) => ($fill ? 'flex: 1;' : '')}
  min-width: 0;
`;

const ActionRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`;
